import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ServicePathProviderService } from '@blk/cwp-ng-framework';
import { API_PARAM_TYPE, ApiParams, OldPortfolio, WebUserDataConstants } from '@ddt-web/domain';
import { HoldingsUtil } from '@ddt-web/generic-utils';
import * as isNil from 'lodash/isNil';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

import { PortfolioParamBuilder } from '../builders/portfolio-param.builder';
import { JsonToPortfolioPipe } from '../pipes/json-to-portfolio.pipe';
import { PortfolioIdUtil } from '../utils/portfolio-id.util';

@Injectable({ providedIn: 'root' })
export class PortfolioDataService {
	private readonly jsonToPortfolioPipe: JsonToPortfolioPipe = new JsonToPortfolioPipe();

	constructor(private http: HttpClient, private servicePathProvider: ServicePathProviderService) { }

	public getPortfoliosFromAPI(rcs: string[] = ['bfa-advisor-data'], params: ApiParams[] = []): Observable<any> {
		const url = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.USER_DATA_URL.GET_ALL;
		const requestParam = { rcs: rcs.join(','), includeChildren: 'false' };
		if (params.length) {
			params.forEach(param => {
				if (param.type === API_PARAM_TYPE.IDENTIFIER.PARAM) { requestParam[param.paramId] = param.value; }
			});
		}
		return this.http.get(url, {
			params: requestParam
		});
	}

	public getModelPortfolios(ids: Array<string>): Observable<any> {
		const url = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.PORTFOLIO_ANALYSIS + '?rc=bfa:model-portfolio-home';
		const portfolios = JSON.stringify(
			ids
				.filter(id => !PortfolioIdUtil.isPartner(id))
				.map(id => {
					return PortfolioParamBuilder.construct().setIdentifier(id).build();
				})
		);

		return this.http.post(url, {
			asOfDateType: 'APPROVED',
			portfolios: portfolios
		});
	}

	/**
	 * @deprecated This is legacy and only used for notifications component
	 */
	public getModelPortfoliosConfigFromAPI(): Observable<any> {
		const url: string = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.MODEL_PORTFOLIOS_CONFIG;
		return this.http.get(url, {
			params: { viewId: 'modelPortfolio@blackrock' }
		});
	}

	public getSavedAndSubscribedPrefs(rcs: string[] = ['bfa:get-saved-subscribed-prefs']): Observable<any> {
		const url = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.USER_DATA_URL.GET_ALL;
		return this.http.get(url, {
			params: { rcs: rcs.join(',') }
		});
	}

	/**
	 * Edit call used in updating list of holdings.
	 *
	 * @param {OldPortfolio} portfolio
	 * @returns {Observable<any>} the observable response of the edit call
	 */
	public edit(portfolio: OldPortfolio): Observable<any> {
		const uniqueId = portfolio.getUniqueId();

		if (!uniqueId) {
			throw throwError(`Portfolio cannot be edited because it is missing uniqueId!`);
		}

		const url = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.USER_DATA_URL.EDIT;
		return this.http.post(url, {
			data: this.formDataParam(portfolio),
			uniqueIds: portfolio.getUniqueId()
		});
	}

	/**
	 * Builds the params data for a given portfolio having:
	 * - type set to portfolio
	 * - subType is CLIENT_CURRENT if provided portfolio has subType of TAX_REPORT, or the provided subType otherwise
	 *
	 * @param {OldPortfolio} portfolio
	 * @returns {string} the data params
	 */
	private formDataParam(portfolio: OldPortfolio): string {
		const subType = portfolio.getSubType() === WebUserDataConstants.SUBTYPE.TAX_REPORT ? WebUserDataConstants.SUBTYPE.CLIENT_CURRENT : portfolio.getSubType();
		const data: Object = {
			type: 'PORTFOLIO',
			subType: subType,
			children: portfolio.getHoldings().map(holding => {
				const returnObj = {
					...HoldingsUtil.retrieveImportantSecurityIdentifiers(holding.asset),
					subType: holding.getSubType(), // subType can either be HOLDING or USER_PORTFOLIO
					identifier: holding.getAsset().getIdentifier(),
					identifierType: holding.getAsset().getIdentifierType(),
					value: holding.getValue()
				};
				// set the weight if the provided portfolio or list of holding have valid weight set
				// WTP updates the weights automatically based on the values provided if weight is not set
				if (!isNil(holding.getWeight()) && holding.getWeight() !== 0) {
					returnObj['weight'] = holding.getWeight();
				}

				return returnObj;
			})
		};

		return JSON.stringify(data);
	}

	public getPortfolio(uniqueId: string): Observable<OldPortfolio> {
		return this.http
			.post(this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.USER_DATA_URL.GET, {
				uniqueIds: uniqueId
			})
			.pipe(
				map((response: any) => {
					try {
						return this.jsonToPortfolioPipe.transform(response.data[0]);
					} catch (error) {
						throwError(error);
					}
				})
			);
	}
}
